import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import styles from './OnboardingFlow.module.css';
import Navbar from '../../components/Layout/Navbar';
import ProfileStep from './steps/ProfileStep';
import BrandBuildStep from './steps/BrandBuildStep';
import AmazonSync from './steps/AmazonSyncStep';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthToken = () =>
  localStorage.getItem('authToken') ||
  localStorage.getItem('idToken') ||
  localStorage.getItem('token') ||
  null;

const STEP_PROFILE = 0;
const STEP_BRAND_BUILD = 1;
const STEP_BRAND_IMPACT = 2;
const STEP_CATALOG_SIZE = 3;
const STEP_LISTING_VOLUME = 4;
const STEP_DAILY_ROLE_FOCUS = 5;
const STEP_TEAM_SIZE = 6;
const STEP_WORKSPACE_TEAM = 7;
const STEP_AMAZON_SYNC = 8;

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [step, setStep] = useState(STEP_PROFILE);
  const [profileDetails, setProfileDetails] = useState({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [stepError, setStepError] = useState('');
  const [profileFirstName, setProfileFirstName] = useState('');
  const [profileMiddleName, setProfileMiddleName] = useState('');
  const [profileLastName, setProfileLastName] = useState('');
  const [profileCompanyName, setProfileCompanyName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileError, setProfileError] = useState('');
  const [brandBuild, setBrandBuild] = useState('');
  const [brandImpact, setBrandImpact] = useState([]);
  const [catalogSize, setCatalogSize] = useState('');
  const [listingVolume, setListingVolume] = useState('');
  const [dailyRoleFocus, setDailyRoleFocus] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [workspaceTeam, setWorkspaceTeam] = useState([]);
  const [brandImpactOthers, setBrandImpactOthers] = useState('');
  const [workspaceTeamOthers, setWorkspaceTeamOthers] = useState('');
  const [direction, setDirection] = useState('forward');

  useEffect(() => {
    if (authContext.user?.questionnaire_complete) {
      setStep(STEP_AMAZON_SYNC);
    }
  }, [authContext.user]);

  const handleStep0Next = async () => {
    if (!profileFirstName.trim()) { setProfileError('Please enter your first name.'); return; }
    if (!profileLastName.trim()) { setProfileError('Please enter your last name.'); return; }
    if (!profileCompanyName.trim()) { setProfileError('Please enter your company name.'); return; }
    if (!profilePhone.trim()) { setProfileError('Please enter your phone number.'); return; }
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(profilePhone.replace(/\D/g, ''))) { setProfileError('Please enter a valid 10-digit phone number.'); return; }
    setProfileError('');

    const details = {
      firstName: profileFirstName,
      middleName: profileMiddleName,
      lastName: profileLastName,
      fullName: [profileFirstName, profileMiddleName, profileLastName].filter(Boolean).join(' '),
      companyName: profileCompanyName,
      phoneNumber: profilePhone,
    };
    setProfileDetails(details);

    const token = getAuthToken();

    // Advance to the Amazon Sync step immediately — the next screen doesn't depend
    // on the persistence calls below, so don't make the user wait on the network.
    authContext.updateUser({
      ...authContext.user,
      questionnaire_complete: true,
    });
    setDirection('forward');
    setStep(STEP_AMAZON_SYNC);

    // Persist in the background (same endpoints/payloads/order as before).
    (async () => {
      // Save profile to backend
      try {
        await fetch(`${API_URL}/auth/handle-onboarding-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            username: authContext.user?.username || authContext.user?.cognito_username,
            uuid: authContext.user?.sub,
            ...details,
          }),
        });
      } catch (err) {
        console.error('Failed to save profile details:', err);
      }

      // Skip the questionnaire slides — tooltips on the dashboard are the new onboarding.
      // Mark questionnaire_complete server-side so ProtectedRoute lets the user through.
      try {
        await fetch(`${API_URL}/auth/onboarding_questionnaire`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            questions: {},
            questionnaire_complete: true,
            details,
          }),
        });
      } catch (err) {
        console.error('Failed to mark questionnaire complete:', err);
      }
    })();
  };

  const handleStep1Next = ({ brandBuild: nextBrandBuild } = {}) => {
    setBrandBuild(nextBrandBuild);
    setDirection('forward');
    setStep(STEP_BRAND_IMPACT);
  };

  const handleStep2Next = ({ brandImpact: nextBrandImpact } = {}) => {
    setBrandImpact(nextBrandImpact);
    setDirection('forward');
    setStep(STEP_CATALOG_SIZE);
  };

  const handleStep3Next = ({ catalogSize: nextCatalogSize } = {}) => {
    setCatalogSize(nextCatalogSize);
    setDirection('forward');
    setStep(STEP_LISTING_VOLUME);
  };

  const handleStep4Next = ({ listingVolume: nextListingVolume } = {}) => {
    setListingVolume(nextListingVolume);
    setDirection('forward');
    setStep(STEP_DAILY_ROLE_FOCUS);
  };

  const handleStep5Next = ({ dailyRoleFocus: nextDailyRoleFocus } = {}) => {
    setDailyRoleFocus(nextDailyRoleFocus);
    setDirection('forward');
    setStep(STEP_TEAM_SIZE);
  };

  const handleStep6Next = ({ teamSize: nextTeamSize } = {}) => {
    setTeamSize(nextTeamSize);
    setDirection('forward');
    setStep(STEP_WORKSPACE_TEAM);
  };

  const handleStep7Next = async ({ workspaceTeam: nextWorkspaceTeam } = {}) => {
    setWorkspaceTeam(nextWorkspaceTeam);

    const resolvedBrandImpact = brandImpact.map((o) =>
      o === 'Others' && brandImpactOthers ? brandImpactOthers : o
    );

    const questions = {
      'How do you primarily build your brand on Amazon?': brandBuild,
      'Where else is your brand making an impact?': resolvedBrandImpact,
      'How many unique products are in your catalog?': catalogSize,
      'What is your typical quarterly listing volume?': listingVolume,
      'Which role best describes your daily focus?': dailyRoleFocus,
      'How big is the team behind the scenes?': teamSize,
      'Who else is joining you on EcomBuddha?': nextWorkspaceTeam,
    };

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
      await fetch(`${API_URL}/auth/onboarding_questionnaire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          questions,
          questionnaire_complete: true,
          details: profileDetails,
        }),
      });
      authContext.updateUser({
        ...authContext.user,
        questionnaire_complete: true,
      });
    } catch (err) {
      console.error('Failed to save questionnaire:', err);
    }

    setDirection('forward');
    setStep(STEP_AMAZON_SYNC);
  };

  const handleGoBack = () => {
    if (step > STEP_PROFILE) {
      setDirection('back');
      setStep(step - 1);
    }
  };

  useEffect(() => {
    setStepError('');
  }, [step]);


  return (
    <div className={styles.page}>
    <Navbar />
    <div className={styles.container}>
      <div className={styles.avatarWrapper}>
        <div className={styles.onboardingBox}>
          {step === STEP_PROFILE && (
            <ProfileStep
              firstName={profileFirstName} setFirstName={setProfileFirstName}
              middleName={profileMiddleName} setMiddleName={setProfileMiddleName}
              lastName={profileLastName} setLastName={setProfileLastName}
              companyName={profileCompanyName} setCompanyName={setProfileCompanyName}
              phoneNumber={profilePhone} setPhoneNumber={setProfilePhone}
              error={profileError}
              onNext={handleStep0Next}
            />
          )}

          {step === STEP_BRAND_BUILD && (
            <BrandBuildStep
              direction={direction}
              valueKey="brandBuild"
              stepIndex={1}
              onGoBack={handleGoBack}
              autoAdvanceOnSelect
              showSubmitButton={false}
              error={stepError}
              questionTitle="How do you primarily build your brand on Amazon?"
              options={[
                'Private Label',
                'Wholesale/Resell',
                'Traders',
                'Dropshipping',
                'KDP/Publishing',
              ]}
              selectedValue={brandBuild}
              onNext={handleStep1Next}
              nextQuestionTitle="Where else is your brand making an impact?"
              nextOptions={['Amazon Global', 'Flipkart', 'Meesho', 'Dropshipping', 'Myntra', 'Shopify', 'Others']}
            />
          )}

          {step === STEP_BRAND_IMPACT && (
            <BrandBuildStep
              direction={direction}
              valueKey="brandImpact"
              stepIndex={2}
              onGoBack={handleGoBack}
              multiSelect
              autoAdvanceOnSelect={false}
              showSubmitButton={false}
              error={stepError}
              onChange={({ brandImpact: val }) => setBrandImpact(val)}
              othersValue={brandImpactOthers}
              onOthersChange={setBrandImpactOthers}
              prevQuestionTitle="How do you primarily build your brand on Amazon?"
              prevOptions={['Private Label', 'Wholesale/Resell', 'Traders', 'Dropshipping', 'KDP/Publishing']}
              questionTitle="Where else is your brand making an impact?"
              options={['Amazon Global', 'Flipkart', 'Meesho', 'Dropshipping', 'Myntra', 'Shopify', 'Others']}
              selectedValue={brandImpact}
              onNext={handleStep2Next}
              nextQuestionTitle="How many unique products are in your catalog?"
              nextOptions={['1-20', '21-100', '101-500', '501-1,500', '1,500+']}
            />
          )}

          {step === STEP_CATALOG_SIZE && (
            <BrandBuildStep
              direction={direction}
              valueKey="catalogSize"
              stepIndex={3}
              topMainTitle="How big is your catalog?"
              topSubtitle="Gives us a sense of the scale you're working at."
              onGoBack={handleGoBack}
              autoAdvanceOnSelect
              showSubmitButton={false}
              error={stepError}
              prevQuestionTitle="Where else is your brand making an impact?"
              prevOptions={['Amazon Global', 'Flipkart', 'Meesho', 'Dropshipping', 'Myntra', 'Shopify', 'Others']}
              questionTitle="How many unique products are in your catalog?"
              options={['1-20', '21-100', '101-500', '501-1,500', '1,500+']}
              selectedValue={catalogSize}
              onNext={handleStep3Next}
              nextQuestionTitle="What is your typical quarterly listing volume?"
              nextOptions={['0-10', '10-50', '50-200', '200-500', '500+']}
            />
          )}

          {step === STEP_LISTING_VOLUME && (
            <BrandBuildStep
              direction={direction}
              valueKey="listingVolume"
              stepIndex={4}
              topMainTitle="How big is your catalog?"
              topSubtitle="Gives us a sense of the scale you're working at."
              onGoBack={handleGoBack}
              autoAdvanceOnSelect
              showSubmitButton={false}
              error={stepError}
              prevQuestionTitle="How many unique products are in your catalog?"
              prevOptions={['1-20', '21-100', '101-500', '501-1,500', '1,500+']}
              questionTitle="What is your typical quarterly listing volume?"
              options={['0-10', '10-50', '50-200', '200-500', '500+']}
              selectedValue={listingVolume}
              onNext={handleStep4Next}
              nextQuestionTitle="Which role best describes your daily focus?"
              nextOptions={['Founder/Owner', 'E-com Manager', 'Account Manager', 'Catalog Specialist', 'Freelancer']}
            />
          )}

          {step === STEP_DAILY_ROLE_FOCUS && (
            <BrandBuildStep
              direction={direction}
              valueKey="dailyRoleFocus"
              stepIndex={5}
              topMainTitle="Team & roles"
              topSubtitle="A quick overview of your team size and roles to set things up accordingly."
              onGoBack={handleGoBack}
              autoAdvanceOnSelect
              showSubmitButton={false}
              error={stepError}
              prevQuestionTitle="What is your typical quarterly listing volume?"
              prevOptions={['0-10', '10-50', '50-200', '200-500', '500+']}
              questionTitle="Which role best describes your daily focus?"
              options={['Founder/Owner', 'E-com Manager', 'Account Manager', 'Catalog Specialist', 'Freelancer']}
              selectedValue={dailyRoleFocus}
              onNext={handleStep5Next}
              nextQuestionTitle="How big is the team behind the scenes?"
              nextOptions={['0-10', '10-50', '50-200', '200+']}
            />
          )}

          {step === STEP_TEAM_SIZE && (
            <BrandBuildStep
              direction={direction}
              valueKey="teamSize"
              stepIndex={6}
              topMainTitle="Team & roles"
              topSubtitle="A quick overview of your team size and roles to set things up accordingly."
              onGoBack={handleGoBack}
              autoAdvanceOnSelect
              showSubmitButton={false}
              error={stepError}
              prevQuestionTitle="Which role best describes your daily focus?"
              prevOptions={['Founder/Owner', 'E-com Manager', 'Account Manager', 'Catalog Specialist', 'Freelancer']}
              questionTitle="How big is the team behind the scenes?"
              options={['0-10', '10-50', '50-200', '200+']}
              selectedValue={teamSize}
              onNext={handleStep6Next}
              nextQuestionTitle="Who else is joining you on EcomBuddha?"
              nextOptions={['Just me', 'E-com manager', 'Content team', 'Agency partner', 'Other']}
            />
          )}

          {step === STEP_WORKSPACE_TEAM && (
            <BrandBuildStep
              direction={direction}
              onGoBack={handleGoBack}
              valueKey="workspaceTeam"
              stepIndex={7}
              topMainTitle="Team & roles"
              topSubtitle="A quick overview of your team size and roles to set things up accordingly."
              multiSelect
              autoAdvanceOnSelect={false}
              onChange={({ workspaceTeam: val }) => setWorkspaceTeam(val)}
              showSubmitButton
              submitButtonLabel="Submit"
              prevQuestionTitle="How big is the team behind the scenes?"
              prevOptions={['0-10', '10-50', '50-200', '200+']}
              questionTitle="Who else is joining you on EcomBuddha?"
              options={['Just me', 'E-com manager', 'Content team', 'Agency partner', 'Other']}
              selectedValue={workspaceTeam}
              onNext={handleStep7Next}
              othersValue={workspaceTeamOthers}
              onOthersChange={setWorkspaceTeamOthers}
            />
          )}

          {step === STEP_AMAZON_SYNC && (
            <AmazonSync
              onSkip={() => navigate('/dashboard')}
              isConnecting={isConnecting}
              onConnect={async () => {
                const token = getAuthToken();
                if (!token) return;
                setIsConnecting(true);
                try {
                  const response = await fetch(`${API_URL}/auth/amazon/login`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  });
                  const data = await response.json();
                  if (data.auth_url) {
                    window.location.href = data.auth_url;
                    return;
                  }
                } catch (err) {
                  console.error('Amazon connect failed:', err);
                }
                setIsConnecting(false);
              }}
            />
          )}


        </div>
      </div>
    </div>
    </div>
  );
};

export default OnboardingFlow;
